/* eslint-disable prefer-const */
import { FormsModule } from '@angular/forms';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, LOCALE_ID, NgZone, Renderer2, AfterViewInit, input, output, inject } from '@angular/core';
import { StyleClassModule } from 'primeng/styleclass';
import type { OmniSearchResponse, Client, PagedResults, ListClientResponse, TENANTSINTERFACE, STAGE_MASTER_MODEL } from '@fitness-force/models';

/** Client-like object for initialData - accepts Client, ListClientResponse, or ClientInfo from task list */
export type MiniSearchClientInput = Client | ListClientResponse | { id?: number; clt_id?: number; full_name?: string; clt_full_name?: string; image?: string };
import { BehaviorSubject, Subject, debounceTime, } from 'rxjs';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { AutoCompleteModule } from 'primeng/autocomplete';
import {  TranslateModule, TranslateService, } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, TENANTS_INFO, } from '@fitness-force/state';
import { ClientService, OmniSearchService } from '@fitness-force/api';
import { ElasticProviderName } from '@fitness-force/enum';

import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ShortNamePipe } from '@fitness-force/pipes/short-name'
import { HighlightSearch } from '@fitness-force/pipes/common-pipes';

@Component({
  selector: 'mini-search',
  templateUrl: './mini-search.component.html',
  styleUrls: ['./mini-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    StyleClassModule,
    FormsModule,
    InputTextModule,
    AvatarModule,
    ChipModule,
    DialogModule,
    AutoCompleteModule,
    HighlightSearch,
    ShortNamePipe,
    TranslateModule,
    ScrollPanelModule,
    AsyncPipe,
    TitleCasePipe
]
})
export class MiniSearchComponent implements OnInit, AfterViewInit {
  private readonly omniSearchService = inject(OmniSearchService);
  private readonly clientService = inject(ClientService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LOCALE_ID);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);

  readonly isReadonly = input(false, { alias: "readonly" });
  readonly isDisabled = input(false, { alias: "disabledctrl" });
  readonly clientIds = input<number[]>([], { alias: "id" });
  /** Pre-selected client objects to avoid redundant API calls when data is already available (e.g. from TaskList) */
  readonly initialData = input<MiniSearchClientInput[]>([]);
  /** When true and initialData is provided, skip the automatic API call to fetch client details */
  readonly skipInitialLoad = input(false);
  clientId = input(0);
  readonly showSelected = input(true);
  readonly showCreateClient = input(true);
  readonly clients = output<any>({ alias: 'clients' });
  readonly removedClient = output<void>({ alias: 'removedClient' });
  readonly createProspect = output<void>({ alias: 'createProspect' });
  readonly totalRecords = input(new BehaviorSubject<number>(0));
  readonly selectedAllData = input(new BehaviorSubject<boolean>(false));

  // Local state properties (not signals)
  currentClientId = 0;
  currentClientIds: number[] = [];
  
  readonly openclientsidebar = output<{
    openclientsidebar: boolean;
    clientid: number;
}>();

  clientIds$ = new BehaviorSubject<number[]>([]);

  paramNMore = { N: 0 };
  paramNList = { N: 0 };
  members$: BehaviorSubject<Client[]> = new BehaviorSubject([] as Client[]);
  selectedClients$ = new BehaviorSubject<ListClientResponse[]>([]);
  showCreateProspectPopup$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  showAllMembers = false;
  searchText = '';
  inputValue = '';

  hideSearchDropdown = true;
  selectedClient$ = new BehaviorSubject<Client | null>(null);
  selectedClient = null;
  stage$ = new BehaviorSubject<STAGE_MASTER_MODEL | null>(null);
  prospectParams: Map<string, any> = new Map();
  tenantId = 0;
  allTenants: any[] = [];
  pageNumber = 0;
  pageSize = 10; // Adjust this value as per your requirement
  query='';
  private readonly searchText$ = new Subject<string>();

  constructor() {
    const client: Client[] = [];
    this.members$.next(client);
  }
  ngOnInit(): void {
    const companyInfoDetail$ = this.store.select(COMPANY_INFO);
    companyInfoDetail$.subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.allTenants = tenant.accessed_tenant;
      if (tenant.selected_tenant.id > 0) {
        this.tenantId = tenant.selected_tenant.id;
      }
    });

    const initialData = this.initialData();
    const skipInitialLoad = this.skipInitialLoad();

    if (skipInitialLoad && initialData && initialData.length > 0) {
      this.initializeFromInitialData(initialData);
      return;
    }

    this.paramNMore = { N: this.selectedAllData().value ? this.totalRecords().value : this.clientIds().length - 3 };
    this.paramNList = { N: this.clientIds().length };

    this.currentClientId = this.clientId();
    const clientIds = this.clientIds();
    if (clientIds.length == 1) {
      this.currentClientId = this.currentClientId || clientIds[0];
      this.currentClientIds = [];
    } else {
      this.currentClientIds = [...clientIds];
    }

    this.clientIds$.next(clientIds);

    if (clientIds.length > 1) {
      this.GetCLientsByClientIds();
    }

    if (this.currentClientId) this.GetCLientsByClientId();
  }

  /**
   * Initialize state from pre-selected client objects (avoids API calls when data comes from TaskList).
   * Normalizes Client/ClientInfo/ListClientResponse to ListClientResponse-like shape for display.
   */
  private initializeFromInitialData(data: MiniSearchClientInput[]): void {
    const normalized = data.map((c) => this.normalizeToListClientResponse(c));
    const ids = normalized.map((c) => c.id);

    this.currentClientIds = ids;
    this.clientIds$.next(ids);
    // Always use ids.length here — initialData contains the actual unique client objects already.
    // totalRecords reflects the report *row* count (e.g. 89 subscriptions) not the unique client count (e.g. 9),
    // so using it would produce an inflated "89 More" badge when only 6 extra clients exist.
    this.paramNMore = { N: ids.length - 3 };
    this.paramNList = { N: ids.length };

    if (ids.length === 1) {
      this.currentClientId = ids[0];
      const client = normalized[0];
      const omniClient = this.toClientShape(client);
      this.selectedClient$.next(omniClient);
      this.clients.emit(omniClient);
    } else {
      this.selectedClients$.next(normalized);
      this.clients.emit(normalized);
    }
  }

  private normalizeToListClientResponse(c: MiniSearchClientInput): ListClientResponse {
    const id = (c as any).id ?? (c as any).clt_id;
    const fullName = (c as any).full_name ?? (c as any).clt_full_name ?? '';
    const image = (c as any).image ?? (c as any).clt_image ?? null;
    return { ...c, id, full_name: fullName, image } as ListClientResponse;
  }

  private toClientShape(c: ListClientResponse): Client {
    return { ...c, clt_id: c.id, clt_full_name: c.full_name, clt_image: c.image ?? undefined } as unknown as Client;
  }

  ngAfterViewInit() {
    this.searchText$.pipe(debounceTime(300)).subscribe(obj => {
      this.searchClientInomniSearch(obj);
    });
  }

  
  GetCLientsByClientIds() {
    this.clientService
      .getProspects(this.getProspectParams, this.tenantId)
      .subscribe((obj1: PagedResults<ListClientResponse>) => {
        this.clientService
          .getClients(this.getClientsParams, this.tenantId)
          .subscribe((obj: PagedResults<ListClientResponse>) => {
            let lClient: any[] = [];
            if (obj?.items?.length) {
              lClient = lClient.concat(obj.items);
            }

            if (obj1?.items?.length) {
              lClient = lClient.concat(obj1.items);
            }

            this.selectedClients$.next(lClient);
            this.clients.emit(lClient);
          });
      });
  }

  GetCLientsByClientId() {
    const request = {
      language: this.locale,
      query: this.currentClientId.toString(),
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        },
      ],
    };

    this.omniSearchService
      .Search(request)
      .subscribe((search: OmniSearchResponse) => {
        if (
          search?.searchResults?.clients?.items?.length
        ) {
          if(this.currentClientId && this.currentClientId > 0)
          {
            const client = search.searchResults.clients.items.find(o => o.clt_id == this.currentClientId)!;
            this.selectedClient$.next(client);
            this.clients.emit(client);
          }
          else{
            const client = search.searchResults.clients.items[0]!;
            this.selectedClient$.next(client);
            this.clients.emit(client);
          }

        }
      });
  }

  search(search: string) {
    this.searchText$.next(search);
  }

  searchClients(event: any) {
    if(event?.query && event.query.length >= 3)
    this.searchText$.next(event.query);
  }

  searchClientInomniSearch(value:string)
  {
    this.query = value;
    const request = {
      language: this.locale,
      query: value,
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        },
      ],
    };

    this.omniSearchService.Search(request).subscribe({
      next: (response: OmniSearchResponse) => {
        const client = response.searchResults.clients.items;
        this.members$.next(client);
      }
    });
  }


  loadMoreClients(event:Event, pageNumber:number)
  {
    event.stopPropagation();
    this.addClass(event);
    this.pageNumber= pageNumber;
    this.pageSize = pageNumber+10;
    const request = {
      language: this.locale,
      query: this.query,
      providers: [{
        provider_name: ElasticProviderName.Clients,
        pagination: { page_number: pageNumber, page_size: this.pageSize }
      }]
    };

    this.omniSearchService.Search(request).subscribe({
      next: (response: OmniSearchResponse) => {
        const client = response.searchResults.clients.items;
        this.members$.next([...this.members$.getValue(),...client]);
        this.removeClass(event);
      },
      error: () => {
        this.removeClass(event);
      },
    });

  }


  setClient(event: any) {
    if (!this.showSelected()) {
      this.clients.emit(event.value);
      this.clearClient();
      return;
    }
    this.selectedClient$.next(event.value);
    this.currentClientId = event.value.clt_id;
    this.members$.next([]);
    this.clients.emit(event.value);
  }

  clearClient() {
    this.selectedClient$.next(null);
    this.selectedClient = null;
    this.currentClientId = 0;
    this.removedClient.emit();
  }

  get getProspectParams(): Array<Map<string, any>> {
    this.prospectParams = new Map();
    this.prospectParams.set('home_club_tenant_id', {
      name: 'home_club_tenant_id',
      value: this.tenantId,
    });
    this.prospectParams.set('Prospect', {
      name: 'client_status',
      value: 'Prospect',
    });
    this.prospectParams.set('Client', {
      name: 'client_status',
      value: 'Client',
    });
    this.prospectParams.set('ClientNonMember', {
      name: 'client_status',
      value: 'ClientNonMember',
    });
    this.prospectParams.set('ExClient', {
      name: 'client_status',
      value: 'ExClient',
    });
    this.prospectParams.set('PageSize', {
      name: 'PageSize',
      value: 50,
    });
    this.prospectParams.set('PageNumber', {
      name: 'PageNumber',
      value: 1,
    });
    this.currentClientIds?.forEach((v: number) => {
      this.prospectParams.set(v.toString(), { name: 'client_id', value: v });
    });

    // this.prospectParams.set(this.clientId.toString(), { name: "client_id", value: this.clientId });

    return [...this.prospectParams.values()];
  }

  get getClientsParams(): Array<Map<string, any>> {
    this.prospectParams = new Map();
    this.prospectParams.set('home_club_tenant_id', {
      name: 'home_club_tenant_id',
      value: this.tenantId,
    });
    this.prospectParams.set('Client', {
      name: 'client_status',
      value: 'Client',
    });
    this.prospectParams.set('ClientNonMember', {
      name: 'client_status',
      value: 'ClientNonMember',
    });
    this.prospectParams.set('ExClient', {
      name: 'client_status',
      value: 'ExClient',
    });
    this.prospectParams.set('PageSize', {
      name: 'PageSize',
      value: 50,
    });
    this.prospectParams.set('PageNumber', {
      name: 'PageNumber',
      value: 1,
    });
    this.currentClientIds?.forEach((v: number) => {
      this.prospectParams.set(v.toString(), { name: 'client_id', value: v });
    });

    // this.prospectParams.set(this.clientId.toString(), { name: "client_id", value: this.clientId });

    return [...this.prospectParams.values()];
  }

  clientStatus(client: Client | null): string {
    if (client && (client.clt_status || (client as { client_status?: string }).client_status)) {
      const raw =
        client.clt_status ?? (client as { client_status?: string }).client_status;
      return this.clientService.getClientStatusHtml(String(raw));
    }
    return '';
  }

  get filteredClient(): Array<any> {
    if (!this.searchText) return this.selectedClients$.value;

    const search = String(this.searchText).toLowerCase();
    return this.selectedClients$.value.filter(
      (item) =>
        String(item.full_name).toLowerCase().indexOf(search) !== -1 ||
        item.id == parseInt(this.searchText)
    );
  }

  client2LetterName(name: string): string {
    if (name) {
      const nameArray: string[] = name.split(' ');
      const newNameArray: string[] = [];
      for (let i = 0; i < nameArray.length; i++) {
        if (newNameArray.length >= 2) break;

        if (nameArray[i]) {
          newNameArray.push(nameArray[i]);
        }
      }
      return newNameArray.join(' ');
    } else {
      return '';
    }
  }

  hidePopup(clientId: number) {
    this.zone.run(() => {
      this.currentClientId = clientId;
      this.GetCLientsByClientId();
      this.showCreateProspectPopup$.next(false);
    });
  }

  OpenPopup() {
    this.createProspect.emit();
  }

  getTenant(client: any) {
    if (client && client.clt_home_club_tenant_id != 0)
      return this.allTenants.find((t: any) => t.id == client.clt_home_club_tenant_id);
    else if (client && client.clt_createdin_tenant_id != 0)
      return this.allTenants.find((t: any) => t.id == client.clt_createdin_tenant_id);
    else return null;
  }

  addClass(event:Event)
  {
    const clickedElement = event.target as HTMLElement;
    const classesToAdd = ['fa-spin', 'fa-spinner'];
    classesToAdd.forEach(className => {
      this.renderer.addClass(clickedElement.firstElementChild, className);
    });
  }

  removeClass(event:Event)
  {
    const clickedElement = event.target as HTMLElement;
    const classesToAdd = ['fa-spin', 'fa-spinner'];
    classesToAdd.forEach(className => {
      this.renderer.removeClass(clickedElement.firstElementChild, className);
    });
  }

  navigateToClientProfile(client:any ) {    
    this.openclientsidebar.emit({openclientsidebar:true,clientid:client.clt_id});
  }

}
